
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";

import { AppContext } from "../../App";
import { IsEmptyObj } from "../../hooks/Method";
import { formatCurrency } from "../../hooks/Currency";

import ViewPurch from "./ViewPurch";
import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Purchases
//------------------------------------------------
const Purchases = (props) => {
  const { t } = useTranslation();

  const {
    _clear,
    _getById,
    _list,
    _type,
    _delete,
    _store,
  } = props;

  const { setLoader } = useContext(AppContext);
  const navigate = useNavigate();

  const reportRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [viewData, setViewData] = useState({});

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Add Purchases
  //------------------------------------------------
  const handleMultPurch = () => {
    navigate("multi-purchase-form");
  };

  //------------------------------------------------
  // Load Purchases by Store
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.Purchase + controller.GetByStore,
        _store.id,
        actions.ActionType.List,
        () => setLoader(false)
      );
    }

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Purchase Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (
      _type !== "purchases" ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => {
      const purchaseItems = Array.isArray(obj.purchItems)
        ? obj.purchItems
        : [];

      const discount = Number(obj.discount) || 0;

      const totalItem = purchaseItems.reduce(
        (sum, item) =>
          sum + Number(item.qty || 0),
        0
      );

      const totalPurchase = purchaseItems.reduce(
        (sum, item) => {
          const quantity = Number(item.qty || 0);
          const price = Number(item.purchPrice || 0);

          const lineTotal = quantity * price;

          return (
            sum +
            lineTotal -
            (discount / 100) * lineTotal
          );
        },
        0
      );

      return {
        ...obj,

        supplier: obj.supplier?.company || "",

        totalItem,

        totalPurch: formatCurrency(
          totalPurchase,
          _store
        ),

        viewReceipt: obj.receipt ? (
          <a
            href={obj.receipt}
            target="_blank"
            rel="noreferrer"
          >
            {t("viewReceipt")}
          </a>
        ) : (
          ""
        ),
      };
    });
  }, [_list, _type, _store, t]);

  //------------------------------------------------
  // Delete Purchase
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteRecordConfirmation"),

      onConfirm: () => {
        const onComplete = (res) => {
          if (res.status === 200) {
            setAlert({
              isOpen: true,
              type: "success",
              title: t("deletedSuccessfully"),
              subTitle: res.msg,
            });
          } else {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: res.msg,
            });
          }
        };

        _delete(
          endpoints.Purchase,
          id,
          actions.ActionType.RmvList,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // View Purchase Details
  //------------------------------------------------
  const handleView = (data) => {
    // Pass original API object to ViewPurch.
    const original = Array.isArray(_list)
      ? _list.find(
          (item) =>
            String(item.id) === String(data.id)
        )
      : null;

    setViewData(original || data);
    setShowForm(true);
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "id", label: t("referenceNo") },
    { id: "supplier", label: t("supplier") },
    { id: "totalItem", label: t("totalItem") },
    { id: "totalPurch", label: t("totalPurchase") },
    { id: "purchDate", label: t("purchaseDate") },
    { id: "viewReceipt", label: t("receipt") },
  ];

  const printColumns = screenColumns.filter(
    (column) =>
      column.id !== "actions" &&
      column.id !== "viewReceipt"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editThisItem")}>
        <IconButton
          onClick={() =>
            navigate(`multi-purchase-form/${data.id}`)
          }
          size="small"
          className="btn-primary mr-5"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteThisItem")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Purchases Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={handleMultPurch}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mr: "10px",
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addPurchases")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("purchases")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("purchases")}
        />
      </Box>

      {/* Normal Purchases Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Purchases List */}
      <Box
        ref={reportRef}
        className="purchases-list-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("purchases")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* Existing Purchase Details Dialog */}
      <Dialog
        show={showForm}
        maxWidth="lg"
        onClose={false}
      >
        {!IsEmptyObj(viewData) && (
          <ViewPurch
            setShowForm={setShowForm}
            viewData={viewData}
          />
        )}
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Purchases);
